import { ref, type Ref } from 'vue'
import type { components, paths } from '../../types/soda-schema'

// Type definitions from the OpenAPI schema
// The public projects endpoint returns ProjectList items (not the full Project type)
export type Project = components['schemas']['ProjectList']
export type ApiResponse<T> = {
  data: T | null
  error: string | null
  loading: boolean
}

// Base API configuration
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL
const TENANT_ID = import.meta.env.VITE_TENANT_ID

/**
 * Generic API fetch function with TypeScript support
 */
async function apiFetch<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`
  
  const defaultOptions: RequestInit = {
    headers: {
      'Content-Type': 'application/json',
      'x-tenantid': TENANT_ID,
      ...options.headers,
    },
    ...options,
  }

  const response = await fetch(url, defaultOptions)
  
  if (!response.ok) {
    throw new Error(`API Error: ${response.status} ${response.statusText}`)
  }
  
  return response.json()
}

/**
 * Composable for fetching projects
 * Following Vue 3 Composition API best practices
 */
export function useProjects(tenantId: string) {
  // Reactive state
  const projects: Ref<Project[]> = ref([])
  const loading = ref(false)
  const error = ref<string | null>(null)

  // Fetch projects function
  const fetchProjects = async () => {
    loading.value = true
    error.value = null
    
    try {
      // Use the OpenAPI schema types for the endpoint
      type ProjectsResponse = paths['/api/projects']['get']['responses']['200']['content']['application/json']
      
      const response = await apiFetch<ProjectsResponse>(
        `/api/projects/public?tenantId=${encodeURIComponent(tenantId)}`
      )
      
      projects.value = response || []
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to fetch projects'
      console.error('Error fetching projects:', err)
    } finally {
      loading.value = false
    }
  }

  // Computed stats based on projects
  const stats = ref({
    totalProjects: 0,
    activeProjects: 0,
    totalAssetsMinted: 0,
    availableForSale: 0
  })

  // Update stats whenever projects change
  const updateStats = () => {
    stats.value = {
      totalProjects: projects.value.length,
      activeProjects: projects.value.filter(p => p.status === 'Active').length,
      totalAssetsMinted: projects.value.reduce((sum, p) => sum + (p.assetsMinted || 0), 0),
      // Assuming availableForSale is assetsMinted minus sold (placeholder logic)
      availableForSale: projects.value.reduce((sum, p) => sum + Math.max(0, (p.assetsMinted || 0) * 0.7), 0)
    }
  }

  return {
    projects,
    loading,
    error,
    stats,
    fetchProjects,
    updateStats
  }
}

/**
 * Additional API functions can be added here
 */
export function useApiCall<T>() {
  const data: Ref<T | null> = ref(null)
  const loading = ref(false)
  const error = ref<string | null>(null)

  const execute = async (endpoint: string, options?: RequestInit) => {
    loading.value = true
    error.value = null
    
    try {
      data.value = await apiFetch<T>(endpoint, options)
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'API call failed'
    } finally {
      loading.value = false
    }
  }

  return {
    data,
    loading,
    error,
    execute
  }
}